// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit, SyncTimesResponse } from '@evolvemep/api-ts';
import type { ColDef, AutoGroupColumnDef } from 'ag-grid-enterprise';

import { isNotNil } from 'helpers/isNotNil';

export type SyncTimesTree = StrongOmit<SyncTimesResponse, 'email'> & {
  children: SyncTimesResponse[];
};

export const getBaseSyncColDefs = ({
  isCompact,
}: {
  isCompact: boolean;
}): ColDef<SyncTimesResponse | SyncTimesTree>[] => [
  { field: 'distinctSessionCount', headerName: 'Unique Sessions', width: 140 },
  { field: 'distinctUserCount', headerName: 'Unique Users', width: 140 },
  { field: 'totalExecutions', headerName: 'Syncs', width: 110 },
  { field: 'totalExecutionFailures', headerName: 'Failed Syncs', hide: isCompact, width: 140 },
  {
    field: 'averageExecutionTime',
    headerName: 'Avg Execution (mins)',
    hide: isCompact,
    width: 170,
    valueFormatter: ({ value }: { value: number | undefined }) => (isNotNil(value) ? (value / 60_000).toFixed(2) : ''),
  },
  {
    field: 'averageRecordsPerActiveDay',
    headerName: 'Average Syncs/Day',
    hide: isCompact,
    width: 160,
    valueFormatter: ({ value }: { value: number | undefined }) => (isNotNil(value) ? value.toFixed(2) : ''),
  },
];

export const documentEmailColDef: AutoGroupColumnDef<SyncTimesTree | SyncTimesResponse> = {
  flex: 1,
  headerName: 'Document/Email',
  headerTooltip: "Top level refers to the document, sub-rows refer to the user's email",
  valueGetter: ({ data }) => (isNotNil(data) ? ('email' in data ? data.email : data.document) : ''),
  width: 300,
  minWidth: 200,
  filter: 'agTextColumnFilter',
  filterParams: {
    maxNumConditions: 1,
    filterOptions: ['contains', 'startsWith', 'endsWith'],
  },
};
